import React from 'react'

import { useTranslation } from 'react-i18next'

import Input from '@components/shared/form/Input'
import { useDataObject } from '@hooks/useDataObject'
import { useSession } from '@stores/globalData/globalData.store'

import type { ValidationErrors } from './CreditCardForm'
import type { FormDataType } from '@components/music/PaymentForm/hooks/usePaymentForm'

/**
 *
  We need to capture the First and Last name of a buyer in order to properly print the authorized use statement, and address them by their first and last name in all emails.

  We already have first and last name fields in a NF User profile, and we should use these fields for the data.

  During the first score purchase, when a user is setting up their CC to buy a score, we supply fields for First and Last name. There will be a separate field for "Name on Credit Card".

  If the user has already added their first and last name to their NF user profile we pre populate the fields with their name and allow them to change it. If no name, we require they enter their first and last name.

*/

interface Props {
  formData: FormDataType
  handleElementChange: (target: HTMLInputElement) => void
}

const UsernameForm: React.FC<Props> = ({ formData, handleElementChange }) => {
  const { t } = useTranslation()
  const session = useSession()
  const [validationErrors, setValidationErrors] = useDataObject<ValidationErrors>({})

  const handleValidity = (element) => {
    setValidationErrors({ [element.name]: element.validationMessage })
  }

  if (session.currentUser?.profile.first_name && session.currentUser?.profile.last_name) return null
  return (
    <>
      <p className="mb-2 font-semibold">{t('content.purchaser')}</p>
      <div className="flex flex-col sm:flex-row" data-testid="username-form">
        <Input
          required
          solidBackground
          className="mr-4 sm:basis-1/2 basis-full grow-0 shrink-0"
          label={t('content.firstName')}
          minLength={1}
          name="first_name"
          value={formData['first_name']}
          onChange={(e) => handleElementChange(e.target)}
          onBlur={(e) => handleValidity(e.target)}
        />
        <Input
          required
          solidBackground
          className="mr-4 sm:basis-1/2 basis-full grow-0 shrink-0"
          label={t('content.lastName')}
          minLength={1}
          name="last_name"
          value={formData['last_name']}
          onChange={(e) => handleElementChange(e.target)}
          onBlur={(e) => handleValidity(e.target)}
        />
      </div>
      <div>
        {validationErrors.first_name && (
          <span className="text-sm text-orange">{validationErrors.first_name}</span>
        )}
        {validationErrors.last_name && (
          <span className="float-right text-sm text-orange">{validationErrors.last_name}</span>
        )}
      </div>
    </>
  )
}

export default UsernameForm
